import { AfterViewInit, Component, ElementRef, Inject, OnDestroy, OnInit, Optional, Self, ViewChild } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, NgControl, ValidatorFn } from '@angular/forms';
import { NgbPopover } from '@ng-bootstrap/ng-bootstrap';

import { map, merge, Observable, Subject, Subscription } from 'rxjs';
import { distinctUntilChanged, startWith, tap } from 'rxjs/operators';
import { APP_CONFIG } from '../../../app.config';
import { AgeService } from '../../../common/age.service';
import { AssistantPopoverService } from '../../../common/assistant-popover.service';
import { ValidationService } from '../../../common/validation.service';
import { isRequired, leapDayValidator } from '../../../common/validators';
import { ValueListsService } from '../../../common/value-lists.service';
import { dateSpecialValues } from '../../../directives/reset-date-special-values.directive';
import { AppConfig } from '../../../models/config/app-config';
import { FormSubGroupControl } from '../../../models/formControls/FormSubGroupControl';
import { ValueListItem } from '../../../models/rest/valuelist/valuelist';
import {
  PassRestrictedCheckboxTextKey
} from '../../../shared/components/pass-restricted-checkbox/pass-restricted-checkbox-texts/pass-restricted-checkbox-texts.component';
import { VisumErfassungSubFormComponent } from '../visum-erfassung-sub-form/visum-erfassung-sub-form.component';

const MAX_KINDER = 6;

@Component({
  selector: 'app-angaben-zur-person',
  templateUrl: './angaben-zur-person.component.html',
  styleUrls: ['./angaben-zur-person.component.scss']
})
export class AngabenZurPersonComponent extends VisumErfassungSubFormComponent implements OnInit, OnDestroy, AfterViewInit {

  private subscriptions: Subscription = new Subscription();

  landListItems: ValueListItem[];
  staatsangehoerigkeitListItems: ValueListItem[];
  geschlechtListItems: ValueListItem[];
  familienstandListItems: ValueListItem[];
  berufListItems: ValueListItem[];

  kindControls: AbstractControl[];

  isEhegatteVisible = false;
  isBerufLangfristigerAufenthaltVisible = false;
  isBerufKurzfristigerAufenthaltVisible = false;
  isFamilienstandSeitVisible = false;
  isAngabenZumUnionsbuergerVisible = false;
  isStellvertreter$: Observable<boolean>;
  selectStellvertreterDefaultCheckbox$: Subject<string> = new Subject();

  // Parental Care taker
  isMinderjaehrige$: Observable<boolean>;
  isMinderjaehrigeAndCVisum$: Subject<boolean> = new Subject();
  isElternErsteZeileRequired = false;

  @ViewChild('angabenZurPersonfirstInputFieldForFocus', { static: true }) angabenZurPersonfirstInputFieldForFocus: ElementRef;
  @ViewChild('angabenZurPersonfirstInputFieldForFocus', {
    read: NgbPopover,
    static: true
  }) angabenZurPersonfirstInputFieldForFocusPopover: NgbPopover;

  isStellVertreterRestrictedCheckboxLabels: Record<string, PassRestrictedCheckboxTextKey> = {
    yes: 'YES',
    no: 'NO',
  };

  constructor(fb: FormBuilder,
              private valueListsService: ValueListsService,
              protected validationService: ValidationService,
              private ageService: AgeService,
              @Optional() @Self() controlDir: NgControl,
              assistantPopoverService: AssistantPopoverService,
              @Inject(APP_CONFIG) public appConfig: AppConfig
  ) {
    super(controlDir, assistantPopoverService);

    this.form = this.wrapFormGroup(fb.group({
      // Personendaten des Antragstellers
      familienname: [null, validationService.getValidators('antragsteller.familienname')],
      isStellvertreter: [null, validationService.getValidators('antragsteller.isStellvertreter')],
      geburtsname: [null, validationService.getValidators('antragsteller.geburtsname')],
      vorname: [null, validationService.getValidators('antragsteller.vorname')],
      geburtsdatum: [null, [...validationService.getValidators('antragsteller.geburtsdatum'), leapDayValidator]],
      alter: null,
      geburtsort: [null, validationService.getValidators('antragsteller.geburtsort')],
      geburtsland: [null, validationService.getValidators('antragsteller.geburtsland')],
      geschlecht: [null, validationService.getValidators('antragsteller.geschlecht')],
      familienstand: [null, validationService.getValidators('antragsteller.familienstand')],
      familienstandSeit: [null, validationService.getValidators('antragsteller.familienstandUngleichLedigUndUnbekanntSeit.value')],
      derzeitigeStaatsangehoerigkeit1: [null, validationService.getValidators('antragsteller.staatsangehoerigkeitListe', false, false, true)],
      derzeitigeStaatsangehoerigkeit2: [null, []],
      derzeitigeStaatsangehoerigkeit3: [null, []],
      staatsangehoerigkeitBeiGeburt1: [null, validationService.getValidators('antragsteller.staatsangehoerigkeitBeiGeburtListe', false, false, true)],
      staatsangehoerigkeitBeiGeburt2: [null, []],
      staatsangehoerigkeitBeiGeburt3: [null, []],
      hasKinder: false,
      kinder: fb.array([]),
      // Angaben zur Ehegattin/zum Ehegatten/zur Lebenspartnerin/zum Lebenspartner
      ehegatteFamilienname: [null, validationService.getValidators('antragsteller.personendaten.ehegatte.familienname')],
      ehegatteGeburtsname: [null, validationService.getValidators('antragsteller.personendaten.ehegatte.geburtsname')],
      ehegatteVorname: [null, validationService.getValidators('antragsteller.personendaten.ehegatte.vorname')],
      ehegatteGeburtsdatum: [null, validationService.getValidators('antragsteller.personendaten.ehegatte.geburtsdatum')],
      ehegatteGeburtsort: [null, validationService.getValidators('antragsteller.personendaten.ehegatte.geburtsort')],
      ehegatteGeburtsland: [null, validationService.getValidators('antragsteller.personendaten.ehegatte.geburtsland')],
      ehegatteWohnort: [null, validationService.getValidators('antragsteller.personendaten.ehegatte.wohnort')],
      ehegatteDerzeitigeStaatsangehoerigkeit1: [null, validationService.getValidators('antragsteller.personendaten.ehegatte.staatsangehoerigkeitListe', false, false, true)],
      ehegatteDerzeitigeStaatsangehoerigkeit2: [null, []],
      ehegatteDerzeitigeStaatsangehoerigkeit3: [null, []],
      ehegatteStaatsangehoerigkeitBeiGeburt1: [null, validationService.getValidators('antragsteller.personendaten.ehegatte.staatsangehoerigkeitBeiGeburtListe', false, false, true)],
      ehegatteStaatsangehoerigkeitBeiGeburt2: [null, []],
      ehegatteStaatsangehoerigkeitBeiGeburt3: [null, []],
      // Eltern des/der Antragstellenden
      familiennameVater: [null, validationService.getValidators('antragsteller.personendaten.eltern.vater.familienname')],
      vornameVater: [null, validationService.getValidators('antragsteller.personendaten.eltern.vater.vorname')],
      staatsangehoerigkeitVater: [null, validationService.getValidators('antragsteller.personendaten.eltern.vater.staatsangehoerigkeitListe', false, false, true)],
      geburtsdatumVater: [null, validationService.getValidators('antragsteller.personendaten.eltern.vater.geburtsdatum')],
      geburtsortVater: [null, validationService.getValidators('antragsteller.personendaten.eltern.vater.geburtsort')],
      wohnortVater: [null, validationService.getValidators('antragsteller.personendaten.eltern.vater.wohnort')],
      familiennameMutter: [null, validationService.getValidators('antragsteller.personendaten.eltern.mutter.familienname')],
      vornameMutter: [null, validationService.getValidators('antragsteller.personendaten.eltern.mutter.vorname')],
      staatsangehoerigkeitMutter: [null, validationService.getValidators('antragsteller.personendaten.eltern.mutter.staatsangehoerigkeitListe', false, false, true)],
      geburtsdatumMutter: [null, validationService.getValidators('antragsteller.personendaten.eltern.mutter.geburtsdatum')],
      geburtsortMutter: [null, validationService.getValidators('antragsteller.personendaten.eltern.mutter.geburtsort')],
      wohnortMutter: [null, validationService.getValidators('antragsteller.personendaten.eltern.mutter.wohnort')],
      anschriftEltern: [null, validationService.getValidators('antragsteller.personendaten.eltern.anschrift')],
      telefonEltern: [null, validationService.getValidators('antragsteller.personendaten.eltern.kontaktdaten.telefon')],
      emailEltern: [null, validationService.getValidators('antragsteller.personendaten.eltern.kontaktdaten.email')],
      // Beruf
      berufAuswahl: [null, validationService.getValidators('antragsteller.personendaten.berufdaten.berufAuswahl')],
      erlernterBerufSonstiges: [null, validationService.getValidators('antragsteller.personendaten.berufdaten.berufSonstiges')],
      derzeitigerBeruf: [null, validationService.getValidators('antragsteller.personendaten.berufdaten.derzeitigeBeruflicheTaetigkeit')],
      derzeitigerBerufSonstiges: [null, validationService.getValidators('antragsteller.personendaten.berufdaten.derzeitigeBeruflicheTaetigkeitSonstiges')],

      isRechtAufFreizuegigkeit: null,
      verwandter: new FormSubGroupControl(),
      beruf: new FormSubGroupControl(),
      stellvertreter: new FormSubGroupControl()
    }));
    this.form.controls.stellvertreter.disable();

    // Validation for Parental Catr taker, only for c-visum and only if subscriber is under 18
    ['familiennameVater', 'vornameVater', 'staatsangehoerigkeitVater'].forEach(name => {
      const ctrl = this.form.get(name);
      if (ctrl) {
        const existingValidators = ctrl.validator ? [ctrl.validator] : [];
        ctrl.setValidators([...existingValidators, this.firstParentRowRequiredValidator]);
        ctrl.updateValueAndValidity({ emitEvent: false });
      }
    });

    // Wird gebraucht für den Loop im HTML Template
    this.kindControls = (<FormArray>this.form.get('kinder')).controls;

    if (this.appConfig.visumAntragsartEnum === 'KURZFRISTIGER_AUFENTHALT') {
      this.form.controls.berufAuswahl.disable();
    }
  }

  protected readonly isRequired = isRequired;

  ngOnInit() {
    super.ngOnInit();

    this.subscriptions.add(this.valueListsService.getValueListItems('land')
    .subscribe((listItems: ValueListItem[]) => this.landListItems = listItems));

    this.subscriptions.add(this.valueListsService.getValueListItems('staatsangehoerigkeit')
    .subscribe((listItems: ValueListItem[]) => this.staatsangehoerigkeitListItems = listItems));

    this.subscriptions.add(this.valueListsService.getValueListItems('geschlecht')
    .subscribe((listItems: ValueListItem[]) => this.geschlechtListItems = listItems));

    this.subscriptions.add(this.valueListsService.getValueListItems('familienstand')
    .subscribe((listItems: ValueListItem[]) => this.familienstandListItems = listItems));

    this.subscriptions.add(this.valueListsService.getValueListItems('beruf')
    .subscribe((listItems: ValueListItem[]) => this.berufListItems = listItems));

    this.subscriptions.add(this.form.controls.hasKinder.valueChanges.subscribe(value => {
      if (value !== this.form.value.hasKinder) {
        this.showOrHideKinder(value);
      }
    }));

    this.subscriptions.add(this.form.controls.isRechtAufFreizuegigkeit.valueChanges.subscribe(value => {
      if (value !== this.form.value.isRechtAufFreizuegigkeit) {
        this.showOrHideAngabenZumUnionsbuerger(value);
        this.showOrHideBeruf(value);
      }
    }));

    this.subscriptions.add(this.form.controls.familienstand.valueChanges.subscribe(value => {
      if (value !== this.form.value.familienstand) {
        this.showOrHideEhegatte(value);
        this.showOrHideFamilienstandSeit(value);
      }
    }));

    this.subscriptions.add(this.form.controls.geburtsdatum.valueChanges.subscribe(value => {
      if (value !== this.form.value.geburtsdatum) {
        if (dateSpecialValues.map(value => value.originalValue).includes(value)) {
          return;
        }
        this.updateAlter(value);
      }
    }));

    // compress ehegatteDerzeitigeStaatsangehoerigkeit
    this.subscriptions.add(this.form.controls.ehegatteDerzeitigeStaatsangehoerigkeit1.valueChanges.subscribe(value => {
      if (value !== this.form.value.ehegatteDerzeitigeStaatsangehoerigkeit1) {
        this.compress('ehegatteDerzeitigeStaatsangehoerigkeit');
      }
    }));

    this.subscriptions.add(this.form.controls.ehegatteDerzeitigeStaatsangehoerigkeit2.valueChanges.subscribe(value => {
      if (value !== this.form.value.ehegatteDerzeitigeStaatsangehoerigkeit2) {
        this.compress('ehegatteDerzeitigeStaatsangehoerigkeit');
      }
    }));

    this.subscriptions.add(this.form.controls.ehegatteDerzeitigeStaatsangehoerigkeit3.valueChanges.subscribe(value => {
      if (value !== this.form.value.ehegatteDerzeitigeStaatsangehoerigkeit3) {
        this.compress('ehegatteDerzeitigeStaatsangehoerigkeit');
      }
    }));

    // ehegatteStaatsangehoerigkeitBeiGeburt
    this.subscriptions.add(this.form.controls.ehegatteStaatsangehoerigkeitBeiGeburt1.valueChanges.subscribe(value => {
      if (value !== this.form.value.ehegatteStaatsangehoerigkeitBeiGeburt1) {
        this.compress('ehegatteStaatsangehoerigkeitBeiGeburt');
      }
    }));

    this.subscriptions.add(this.form.controls.ehegatteStaatsangehoerigkeitBeiGeburt2.valueChanges.subscribe(value => {
      if (value !== this.form.value.ehegatteStaatsangehoerigkeitBeiGeburt2) {
        this.compress('ehegatteStaatsangehoerigkeitBeiGeburt');
      }
    }));

    this.subscriptions.add(this.form.controls.ehegatteStaatsangehoerigkeitBeiGeburt3.valueChanges.subscribe(value => {
      if (value !== this.form.value.ehegatteStaatsangehoerigkeitBeiGeburt3) {
        this.compress('ehegatteStaatsangehoerigkeitBeiGeburt');
      }
    }));

    // compress derzeitigeStaatsangehoerigkeit
    this.subscriptions.add(this.form.controls.derzeitigeStaatsangehoerigkeit1.valueChanges.subscribe(value => {
      if (value !== this.form.value.derzeitigeStaatsangehoerigkeit1) {
        this.compress('derzeitigeStaatsangehoerigkeit');
      }
    }));

    this.subscriptions.add(this.form.controls.derzeitigeStaatsangehoerigkeit2.valueChanges.subscribe(value => {
      if (value !== this.form.value.derzeitigeStaatsangehoerigkeit2) {
        this.compress('derzeitigeStaatsangehoerigkeit');
      }
    }));

    this.subscriptions.add(this.form.controls.derzeitigeStaatsangehoerigkeit3.valueChanges.subscribe(value => {
      if (value !== this.form.value.derzeitigeStaatsangehoerigkeit3) {
        this.compress('derzeitigeStaatsangehoerigkeit');
      }
    }));

    // compress staatsangehoerigkeitBeiGeburt
    this.subscriptions.add(this.form.controls.staatsangehoerigkeitBeiGeburt1.valueChanges.subscribe(value => {
      if (value !== this.form.value.staatsangehoerigkeitBeiGeburt1) {
        this.compress('staatsangehoerigkeitBeiGeburt');
      }
    }));

    this.subscriptions.add(this.form.controls.staatsangehoerigkeitBeiGeburt2.valueChanges.subscribe(value => {
      if (value !== this.form.value.staatsangehoerigkeitBeiGeburt2) {
        this.compress('staatsangehoerigkeitBeiGeburt');
      }
    }));

    this.subscriptions.add(this.form.controls.staatsangehoerigkeitBeiGeburt3.valueChanges.subscribe(value => {
      if (value !== this.form.value.staatsangehoerigkeitBeiGeburt3) {
        this.compress('staatsangehoerigkeitBeiGeburt');
      }
    }));

    this.isStellvertreter$ = this.form.controls.isStellvertreter.valueChanges.pipe(
      tap(value => this.setStellvertreterCheckboxDefaultValue(value)),
      map(value => this.mapStellvertreterCheckboxValueToBoolean(value)),
      map((value) => this.setIsStellvertreterValueAndEnableOrDisableStellvertreterForm(value)));

    this.isMinderjaehrige$ = this.createIsMinderjaehrige$(this.form).pipe(map(isMinor => {
      const required = isMinor && this.appConfig.visumAntragsartEnum === 'KURZFRISTIGER_AUFENTHALT';
      this.isElternErsteZeileRequired = required;
      this.isMinderjaehrigeAndCVisum$.next(required);

      ['familiennameVater', 'vornameVater', 'staatsangehoerigkeitVater'].forEach(name => {
        const ctrl = this.form.get(name);
        if (ctrl) {
          ctrl.updateValueAndValidity();
        }
      });
      return required;
    }));
  }

  checkParentControlErrors() {
    return this.form.controls.familiennameVater.errors || this.form.controls.vornameVater.errors || this.form.controls.staatsangehoerigkeitVater.errors;
  }

  private setStellvertreterCheckboxDefaultValue(value: boolean | string | null) {
    if (value === null) {
      this.selectStellvertreterDefaultCheckbox$.next(null);
      return;
    }

    const shouldSetDefaultValue = typeof value === "boolean";
    if (shouldSetDefaultValue) {
      value ? this.selectStellvertreterDefaultCheckbox$.next("no") : this.selectStellvertreterDefaultCheckbox$.next("yes");
    }
  }

  private mapStellvertreterCheckboxValueToBoolean(value: string | boolean | null) {
    if (value === null) {
      return null;
    }

    if (typeof value === "boolean") {
      return value;
    }

    return value !== "yes";
  }

  private setIsStellvertreterValueAndEnableOrDisableStellvertreterForm(value: boolean) {
    this.form.controls.isStellvertreter.setValue(value, {emitEvent: false});
    value ? this.form.controls.stellvertreter.enable() : this.form.controls.stellvertreter.disable();
    return value;
  }

  private updateAlter(geburtsdatum: string) {
    if (geburtsdatum && geburtsdatum.length === 10) {
      // Per REST Service wird asynchron das Alter berechnet und wenn das Ergebnis da ist,
      // wird ein Statusupdate für die Komponente durchgeführt
      this.ageService.calculateAge(geburtsdatum).subscribe(
        {
          next: (ageFromService) => {
            this.form.patchValue({ alter: ageFromService });
          },
          error: () => {
            this.form.patchValue({ alter: undefined });
          }
        }
      );
    } else {
      this.form.patchValue({ alter: undefined });
    }
  }

  private createIsMinderjaehrige$(form: FormGroup): Observable<boolean> {
    const geburtsdatumControl = form.get('geburtsdatum');
    return merge(
      geburtsdatumControl.valueChanges.pipe(startWith(geburtsdatumControl.value)),
      form.statusChanges.pipe(startWith(form.status))
    ).pipe(
      map(() => form.value.alter != null && form.value.alter.minValue < 18),
      distinctUntilChanged()
    );
  }

  ngAfterViewInit() {
    super.ngAfterViewInit();

    if (!this.getAssistantService().getLastFocusedFormField()) {
      this.getAssistantService().setLastFocusedFormField({
        field: <HTMLElement>this.angabenZurPersonfirstInputFieldForFocus.nativeElement,
        popoverForField: this.angabenZurPersonfirstInputFieldForFocusPopover
      });
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  // wird beim laden eines antrages aufgerufen
  onValidatorUpdate() {
    super.onValidatorUpdate();
    this.updateAlter(this.form.value.geburtsdatum);
    this.showOrHideBeruf(this.form.value.isRechtAufFreizuegigkeit);
    this.showOrHideAngabenZumUnionsbuerger(this.form.value.isRechtAufFreizuegigkeit);
    this.showOrHideEhegatte(this.form.value.familienstand);
    this.showOrHideFamilienstandSeit(this.form.value.familienstand);
    this.showElternForm();
    this.showOrHideKinder(this.form.value.hasKinder);
  }

  // Parental Care taker
  private firstParentRowRequiredValidator: ValidatorFn = (control: AbstractControl) => {
    // Nur bei C-Visum UND wenn Minderjährige:
    if (!this.isElternErsteZeileRequired ||
      this.appConfig.visumAntragsartEnum !== 'KURZFRISTIGER_AUFENTHALT') {
      return null;
    }

    const value = control.value;
    return value == null || value === '' ? { required: true } : null;
  };

  isAddKindVisible() {
    const kinderList: FormArray = <FormArray>this.form.get('kinder');
    return kinderList.length !== MAX_KINDER;
  }

  addKind(): void {
    const kinderList: FormArray = <FormArray>this.form.get('kinder');
    if (kinderList.length < MAX_KINDER) {
      kinderList.push(new FormSubGroupControl());
    }
  }

  removeKind(index: number): void {
    const kinderList: FormArray = <FormArray>this.form.get('kinder');
    kinderList.removeAt(index);
  }

  private showOrHideKinder(hasKinder: boolean) {
    if (this.appConfig.isHasKinderActive && hasKinder) {
      this.kindControls.forEach(control => {
        control.enable();
      });
      if (this.form.value.kinder.length === 0) {
        this.addKind();
      }
    } else {
      this.kindControls.forEach(control => {
        control.disable();
      });
    }
  }

  private showOrHideEhegatte(familienstand: string) {
    if (this.appConfig.visumAntragsartEnum === 'KURZFRISTIGER_AUFENTHALT') {
      this.isEhegatteVisible = false;
    } else {
      this.isEhegatteVisible = [
        'verheiratet',
        'getrennt lebend',
        'eingetr. Partnerschaft'
      ].indexOf(familienstand) >= 0;
    }

    if (this.isEhegatteVisible) {
      this.form.controls.ehegatteFamilienname.enable();
      this.form.controls.ehegatteGeburtsname.enable();
      this.form.controls.ehegatteVorname.enable();
      this.form.controls.ehegatteGeburtsdatum.enable();
      this.form.controls.ehegatteGeburtsort.enable();
      this.form.controls.ehegatteGeburtsland.enable();
      this.form.controls.ehegatteWohnort.enable();
      this.form.controls.ehegatteDerzeitigeStaatsangehoerigkeit1.enable();
      this.form.controls.ehegatteStaatsangehoerigkeitBeiGeburt1.enable();
    } else {
      this.form.controls.ehegatteFamilienname.disable();
      this.form.controls.ehegatteGeburtsname.disable();
      this.form.controls.ehegatteVorname.disable();
      this.form.controls.ehegatteGeburtsdatum.disable();
      this.form.controls.ehegatteGeburtsort.disable();
      this.form.controls.ehegatteGeburtsland.disable();
      this.form.controls.ehegatteWohnort.disable();
      this.form.controls.ehegatteDerzeitigeStaatsangehoerigkeit1.disable();
      this.form.controls.ehegatteStaatsangehoerigkeitBeiGeburt1.disable();
    }
  }

  private showOrHideFamilienstandSeit(familienstand: string) {
    if (this.appConfig.visumAntragsartEnum === 'KURZFRISTIGER_AUFENTHALT') {
      this.isFamilienstandSeitVisible = false;
    } else {
      this.isFamilienstandSeitVisible = [
        'verheiratet',
        'getrennt lebend',
        'geschieden',
        'verwitwet',
        'eingetr. Partnerschaft'
      ].indexOf(familienstand) >= 0;
    }

    if (this.isFamilienstandSeitVisible) {
      this.form.controls.familienstandSeit.enable();
    } else {
      this.form.controls.familienstandSeit.disable();
    }
  }

  private showOrHideAngabenZumUnionsbuerger(isRechtAufFreizuegigkeit: boolean) {
    if (this.appConfig.visumAntragsartEnum === 'KURZFRISTIGER_AUFENTHALT') {
      this.isAngabenZumUnionsbuergerVisible = isRechtAufFreizuegigkeit === true;
    } else {
      this.isAngabenZumUnionsbuergerVisible = false;
    }

    this.enableOrDisableControlWithTimeout(this.isAngabenZumUnionsbuergerVisible, this.form.controls.verwandter);
  }

  private showOrHideBeruf(isRechtAufFreizuegigkeit: boolean) {
    if (this.appConfig.visumAntragsartEnum === 'LANGFRISTIGER_AUFENTHALT') {
      this.isBerufKurzfristigerAufenthaltVisible = false;
      this.isBerufLangfristigerAufenthaltVisible = true;
    } else if (this.appConfig.visumAntragsartEnum === 'KURZFRISTIGER_AUFENTHALT') {
      this.isBerufLangfristigerAufenthaltVisible = false;
      this.isBerufKurzfristigerAufenthaltVisible = isRechtAufFreizuegigkeit !== true;
    }

    if (this.isBerufKurzfristigerAufenthaltVisible) {
      this.form.controls.beruf.enable();
    } else {
      this.form.controls.beruf.disable();
    }

    if (this.isBerufLangfristigerAufenthaltVisible) {
      this.form.controls.berufAuswahl.enable();
      this.form.controls.erlernterBerufSonstiges.enable();
      this.form.controls.derzeitigerBeruf.enable();
      this.form.controls.derzeitigerBerufSonstiges.enable();
    } else {
      this.form.controls.berufAuswahl.disable();
      this.form.controls.erlernterBerufSonstiges.disable();
      this.form.controls.derzeitigerBeruf.disable();
      this.form.controls.derzeitigerBerufSonstiges.disable();
    }
  }

  private showElternForm() {

    this.form.controls.familiennameVater.enable();
    this.form.controls.vornameVater.enable();
    this.form.controls.staatsangehoerigkeitVater.enable();
    this.form.controls.geburtsdatumVater.enable();
    this.form.controls.geburtsortVater.enable();
    this.form.controls.wohnortVater.enable();
    this.form.controls.familiennameMutter.enable();
    this.form.controls.vornameMutter.enable();
    this.form.controls.staatsangehoerigkeitMutter.enable();
    this.form.controls.geburtsdatumMutter.enable();
    this.form.controls.geburtsortMutter.enable();
    this.form.controls.wohnortMutter.enable();
    this.form.controls.anschriftEltern.enable();
    this.form.controls.telefonEltern.enable();
    this.form.controls.emailEltern.enable();
  }

}
